import React, { memo, useCallback, useEffect, useState } from 'react'
import { inject, observer } from 'mobx-react'
import { useFormik } from 'formik'
import { Upload, Button, Popconfirm, Tooltip } from 'antd'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import {
  ACL_ACTION_TYPE,
  DIGITAL_SIGN_PROVIDER,
  DIGITAL_TYPE_SIGN_SYSTEM,
  DOCUMENT_TYPE,
} from '../../constants'
import AuthoritiesActionModal from './AuthoritiesActionModal'
import {
  UploadFileListItem,
  UploadFileListWrapper,
} from './InternalDocumentIncomingDocCreatePageStyled'
import {
  DeleteOutlined,
  FileTextOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import utils from '../../utils'
import { toJS } from 'mobx'
import PopupRenameFile from '../../components/PopupRenameFile/PopupRenameFile'
import * as yup from 'yup'
import { useParams } from 'react-router-dom'
import AccessControlAction from '../../components/AccessControlAction/AccessControlAction'

const InternalDocumentIncomingUpdate = props => {
  const {
    open,
    handleClose,
    authorityStore,
    bookStore,
    loadingAnimationStore,
    fileStore,
    signedDocumentStore,
    internalDocumentStore,
    createMessage,
  } = props
  const { authorityIssuedsList } = authorityStore
  const [fileList, setFileList] = useState([])
  const { bookGroupList, bookListOfGroup } = bookStore
  const {
    getInternalDocumentIncomingById,
    selectedInternalDocumentIncoming,
    updateInternalDocument,
  } = internalDocumentStore
  const { documentId } = useParams()
  const [attachmentsMessage, setAttachmentsMessage] = useState(false)
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      update_type: 'INCOMING',
      authority_issued_id:
        selectedInternalDocumentIncoming.authority_issued_id?.id,
      attachments: { new_items: [], remove_items: [] },
      book_group_id: selectedInternalDocumentIncoming.book_group_id,
      book_id: selectedInternalDocumentIncoming.book_id,
      date_issued: selectedInternalDocumentIncoming.date_issued,
      document_number: selectedInternalDocumentIncoming.document_number,
      incoming_number: selectedInternalDocumentIncoming.incoming_number,
      outgoing_date: selectedInternalDocumentIncoming.outgoing_date,
      signer: selectedInternalDocumentIncoming.signer,
      title: selectedInternalDocumentIncoming.title,
      urgency_level: selectedInternalDocumentIncoming.urgency_level,
    },
    validationSchema: yup.object().shape({
      document_number: yup
        .string()
        .required('Vui lòng nhập số hiệu!')
        .matches(/^(?=.*\S).+$/, 'Vui lòng nhập số hiệu!'),
      incoming_number: yup
        .string()
        .required('Vui lòng nhập số đến!')
        .matches(/^(?=.*\S).+$/, 'Vui lòng nhập số đến!'),
      title: yup
        .string()
        .required('Vui lòng nhập trích yếu!')
        .matches(/^(?=.*\S).+$/, 'Vui lòng nhập trích yếu!'),
      outgoing_date: yup.string().required('Vui lòng chọn ngày đến!'),
    }),
    onSubmit: async values => {
      if (fileList.length === 0 && fileListUpdate.length === 0) {
        setAttachmentsMessage(true)
      } else {
        try {
          loadingAnimationStore.showSpinner(true)
          await updateInternalDocument(documentId, values)
          loadingAnimationStore.showSpinner(false)
          createMessage('Cập nhật văn bản thành công!', true, 'success')
        } catch (err) {
          // console.log(err);
        } finally {
          handleCloseForm()
          getInternalDocumentIncomingById(documentId)
        }
      }
    },
  })

  useEffect(() => {
    if (open) setFileListUpdate(selectedInternalDocumentIncoming.attachments)
  }, [open, selectedInternalDocumentIncoming])

  const handleSelectBookGroupId = async bookGroupId => {
    if (!bookGroupId) createMessage('Chưa có nhóm sổ văn bản!', true, 'warning')
    formik.setFieldValue('book_group_id', bookGroupId)
    loadingAnimationStore.showSpinner(true)
    try {
      const bookListOfGroup = await bookStore.getBookByBookGroupId(bookGroupId)
      formik.setFieldValue('book_id', bookListOfGroup.data[0].id)
    } catch (error) {
      createMessage(error?.vi, true, 'error')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  const handleSelectBookId = bookId => {
    if (!bookId) createMessage('Chưa có sổ văn bản!', true, 'warning')
    formik.setFieldValue('book_id', bookId)
  }

  const [fileListUpdate, setFileListUpdate] = useState([])

  const updateAttachments = useCallback(async () => {
    const fileSignedList = fileList
      .filter(file => file.isSigned)
      .map(file => file.id)
    const uploadFileList = fileList.map(file => file.originFileObj)
    const batchUploadArr = []
    uploadFileList.forEach(file => {
      const formData = new FormData()
      formData.append('file', file)
      batchUploadArr.push(fileStore.uploadFile(formData))
    })
    const response = await Promise.all(batchUploadArr)
    const new_items = []
    const remove_items = []
    const originFiles = toJS(selectedInternalDocumentIncoming.attachments)?.map(
      file => file.id
    )
    const afterFiles = [
      ...response.map(el => el.data.file_id),
      ...fileListUpdate.map(el => el.id),
      ...fileSignedList,
    ]
    afterFiles?.forEach(el => {
      if (!originFiles.includes(el)) {
        new_items.push(el)
      }
    })
    originFiles?.forEach(el => {
      if (!afterFiles.includes(el)) {
        remove_items.push(el)
      }
    })
    formik.setFieldValue('attachments', {
      new_items: new_items,
      remove_items: remove_items,
    })
  }, [fileList, fileListUpdate])

  const handleRemoveFileUpdate = useCallback(
    file_id => {
      const newFileList = fileListUpdate.filter(file => file.id !== file_id)
      setFileListUpdate(newFileList)
    },
    [fileListUpdate]
  )

  useEffect(() => {
    updateAttachments()
  }, [updateAttachments])

  const [advanceFilterID, setAdvanceFilterID] = useState({
    date_by: null,
    month: '',
    tag: [],
  })

  const [advanceFilterDI, setAdvanceFilterDI] = useState({
    date_by: null,
    month: '',
    tag: [],
  })

  const handleChangeIncomingDate = value => {
    setAdvanceFilterID({
      ...advanceFilterID,
      month: value,
    })
    formik.setFieldValue('outgoing_date', value)
  }

  const handleChangeDateIssued = value => {
    setAdvanceFilterDI({
      ...advanceFilterDI,
      month: value,
    })
    formik.setFieldValue('date_issued', value)
  }

  const [selectedOption, setSelectedOption] = useState(null)
  useEffect(() => {
    setSelectedOption(selectedInternalDocumentIncoming.urgency_level)
  }, [selectedInternalDocumentIncoming])
  const handleSelectChange = e => {
    setSelectedOption(e.target.value)
  }
  const handleClear = () => {
    setSelectedOption('')
  }

  useEffect(() => {
    if (open) {
      ;(async () => {
        loadingAnimationStore.showSpinner(true)
        try {
          authorityStore.setAuthorityIssuedsQueryParams({
            type: DOCUMENT_TYPE.INCOMING,
            key: null,
          })
          await authorityStore.getAuthorityIssueds()
          await bookStore.getBookGroup('DEN')
          await bookStore.getBookByBookGroupId(
            selectedInternalDocumentIncoming.book_group_id
          )
        } catch (err) {
          createMessage(err?.vi || 'Đã có lỗi xảy ra!', true, 'error')
        } finally {
          loadingAnimationStore.showSpinner(false)
        }
      })()
      return () => {
        bookStore.clearStore()
        signedDocumentStore.clearFilePDFSign()
      }
    }
  }, [open, selectedInternalDocumentIncoming.book_group_id])

  const [isVisibleAuthoritiesActionModal, setIsVisbleAuthoritiesActionModal] =
    useState(false)

  const handleToggleAuthoritiesActionModal = bool => event => {
    setIsVisbleAuthoritiesActionModal(bool)
  }

  useEffect(() => {
    if (selectedOption === '' || selectedOption === null)
      formik.setFieldValue('urgency_level', null)
    else formik.setFieldValue('urgency_level', selectedOption)
  }, [selectedOption])

  const handleCloseForm = () => {
    handleClose()
    formik.resetForm()
    setSelectedOption(selectedInternalDocumentIncoming.urgency_level)
    setFileList([])
    setAttachmentsMessage(false)
  }

  const handleChangeFile = info => {
    if (info.fileList.length === 0) {
      formik.setFieldValue('file_upload', undefined)
      setFileList([])
      return
    }
    const filteredFileList = info.fileList.filter(
      (elem, index, fileList) =>
        fileList.findIndex(file => file.name === elem.name) === index
    )
    setFileList(filteredFileList)
  }

  const handleRemoveFileFromUploadList = useCallback(
    fileUID => {
      const newFileList = fileList.filter(file => file.uid !== fileUID)
      setFileList(newFileList)
      if (newFileList.length === 0) {
        formik.setFieldValue('attachments', {
          new_items: [],
          remove_items: [],
        })
      }
    },
    [fileList]
  )

  const [dataSignSavis, setDataSignSavis] = useState()

  const handleSignSavis = (file, index) => {
    loadingAnimationStore.showSpinner(true)
    const formData = new FormData()
    formData.append('file', file.originFileObj)
    fileStore
      .uploadFile(formData)
      .then(res => {
        const dataSubmit = {
          image: '',
          reason: 'Signed',
          location: 'VietNam',
          provider: DIGITAL_SIGN_PROVIDER,
          contactInfo: '',
          type: DIGITAL_TYPE_SIGN_SYSTEM,
          isVisible: 1,
          page: 1,
          llx: 0,
          lly: 790,
          urx: 300,
          ury: 750,
          listSignFile: [
            {
              fileId: res.data.file_id,
            },
          ],
        }
        signedDocumentStore
          .signDocumentSavis(dataSubmit)
          .then(res => {
            const { fileSignId, fileOriginName, fileSignName } =
              res.data.listSignFile[0]
            const newFileList = [...fileList]
            newFileList[index] = {
              name: fileSignName,
              id: fileSignId,
              uid: file.uid,
              isSigned: true,
              type: 'application/pdf',
            }
            setFileList(newFileList)
            loadingAnimationStore.showSpinner(false)
            createMessage(
              `Văn bản "${fileOriginName}" đã được kí`,
              true,
              'success'
            ).then()
          })
          .catch(err => {
            loadingAnimationStore.showSpinner(false)
            return createMessage(err?.vi || 'Ký số thất bại!', true, 'error')
          })
      })
      .catch(() => loadingAnimationStore.showSpinner(false))
  }

  const handleBeforeSign = (file, index) => {
    setDataSignSavis({
      file: file,
      index: index,
    })
  }

  const [fileExt, setFileExt] = useState(undefined)
  const [fileTargetRename, setFileTargetRename] = useState(null)
  const [isRenameFileUpdate, setIsRenameFileUpdate] = useState(false)
  const [isModalVisibleRenameFile, setIsModalVisibleRenameFile] =
    useState(false)
  const [fieldsRenameFile, setFieldsRenameFile] = useState([
    {
      name: ['rename'],
      value: '',
    },
  ])

  const handleOpenPopupRenameFile = (file, index) => {
    setFieldsRenameFile([
      {
        name: ['rename'],
        value: utils.removeExtensionFile(file.name),
      },
    ])
    setFileExt('.' + utils.getExtensionFile(file.name))
    setFileTargetRename({
      ...file,
      index: index,
    })
    setIsModalVisibleRenameFile(true)
  }

  const renameFile = (originalFile, newName) => {
    return new File([originalFile], newName, {
      type: originalFile.type,
      lastModified: originalFile.lastModified,
    })
  }

  const submitRenameFile = async value => {
    const index = fileTargetRename?.index

    // Đổi tên file update
    if (isRenameFileUpdate) {
      await fileStore.handleRenameFile(
        fileTargetRename.id,
        value.rename + fileExt
      )
      const fileUpdateAfterRename = [...fileListUpdate]
      fileUpdateAfterRename[index].name = value.rename + fileExt
      setFileListUpdate(fileUpdateAfterRename)
      setIsModalVisibleRenameFile(false)
      createMessage('Đổi tên file thành công', true, 'success')
      return
    }
    // đổi tên file nhận từ thiết bị (file chưa ký)
    if (fileTargetRename.originFileObj) {
      const newFileList = [...fileList]
      newFileList[index].name = value.rename + fileExt
      newFileList[index].originFileObj = renameFile(
        newFileList[index].originFileObj,
        newFileList[index].name
      )
      setFileList(newFileList)
      setIsModalVisibleRenameFile(false)
      createMessage('Đổi tên file thành công', true, 'success')
    }
    // đổi tên file nhận từ thiết bị (file đã ký)
    if (!fileTargetRename.originFileObj) {
      await fileStore.handleRenameFile(
        fileTargetRename.id,
        value.rename + fileExt
      )
      setIsModalVisibleRenameFile(false)
      const newFileList = [...fileList]
      newFileList[index].name = value.rename + fileExt
      setFileList(newFileList)
      createMessage('Đổi tên file thành công', true, 'success')
    }
  }

  const renderListFileUpdate =
    fileListUpdate &&
    fileListUpdate.map((file, index) => (
      <UploadFileListItem key={file.id}>
        <FileTextOutlined style={{ color: '#1890FF' }} />
        <span style={{ color: '#1890FF' }}>{file.name}</span>
        <Button
          className={'rename'}
          type="text"
          onClick={() => {
            setIsRenameFileUpdate(true)
            handleOpenPopupRenameFile(file, index)
          }}>
          Đổi tên
        </Button>
        <Tooltip title={'Xoá tập tin'}>
          <DeleteOutlined
            onClick={() => handleRemoveFileUpdate(file.id, index)}
          />
        </Tooltip>
      </UploadFileListItem>
    ))

  const renderListFile = fileList.map((file, index) => (
    <UploadFileListItem
      key={file.uid || file.id}
      isSigned={file.isSigned}
      isNotPDF={file.type !== 'application/pdf'}>
      <FileTextOutlined />
      <span>{file.name}</span>
      {file.isSigned ? (
        <Button
          className={'digital-signature'}
          type="text"
          onClick={() => createMessage('Đã ký!', true, 'info')}>
          Đã ký
        </Button>
      ) : file.type !== 'application/pdf' ? (
        // <AccessControlAction aclActionType={ACL_ACTION_TYPE.ky_so__INCOMING}>
        <Button
          className={'digital-signature'}
          type="text"
          onClick={() =>
            createMessage(
              'Chỉ ký được file PDF, vui lòng chọn file khác!',
              true,
              'info'
            )
          }>
          Ký số
        </Button>
      ) : (
        // </AccessControlAction>
        // <AccessControlAction aclActionType={ACL_ACTION_TYPE.ky_so__INCOMING}>
        <Popconfirm
          placement="top"
          title={'Hãy chọn loại ký'}
          cancelText="Ký mặc đinh"
          onCancel={() => handleSignSavis(file, index)}>
          <Button
            className={'digital-signature'}
            type="text"
            onClick={() => handleBeforeSign(file, index)}>
            Ký số
          </Button>
        </Popconfirm>
        // </AccessControlAction>
      )}
      {
        <Button
          className={'rename'}
          type="text"
          onClick={() => {
            setIsRenameFileUpdate(false)
            handleOpenPopupRenameFile(file, index)
          }}>
          Đổi tên
        </Button>
      }
      <Tooltip title={'Xoá tập tin'}>
        <DeleteOutlined
          onClick={() => handleRemoveFileFromUploadList(file.uid, index)}
        />
      </Tooltip>
    </UploadFileListItem>
  ))

  useEffect(() => {
    if (fileList.length === 0 && fileListUpdate.length === 0)
      setAttachmentsMessage(true)
    else setAttachmentsMessage(false)
  }, [fileList, fileListUpdate])

  const [warningSH, setWarningSH] = useState({})

  const validateDocumentNumber = async e => {
    formik.handleChange(e)
    if (e.target.value && e.target.value.trim() === '') {
      return Promise.reject('Cần nhập ký tự, vui lòng không bỏ trống')
    } else {
      try {
        if (
          e.target.value !== selectedInternalDocumentIncoming.document_number
        ) {
          const response = await internalDocumentStore.getWarningDocumentNumber(
            e.target.value,
            'INCOMING'
          )
          if (response.data.status === 'WARNING') {
            setWarningSH(response.data)
            return Promise.reject(response.data.message)
          } else {
            setWarningSH(response.data)
            return Promise.resolve()
          }
        }
      } catch (error) {
        // console.log(error);
        createMessage(error?.vi || 'Đã có lỗi xảy ra!', true, 'error')
      }
    }
  }

  useEffect(() => {
    formik.setErrors({})
    formik.setTouched({})
    setWarningSH(null)
  }, [open])

  return (
    <Dialog
      open={open}
      onClose={handleCloseForm}
      scroll={'paper'}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      className="doc-popup"
      fullWidth
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100vw',
            maxWidth: '100vw',
            mx: 0,
            zIndex: 50,
          },
        },
      }}>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle
          id="scroll-dialog-title"
          className="text-xl font-bold text-[#ffffff] "
          sx={{
            fontWeight: 700,
            backgroundColor: '#2C65AC',
          }}>
          <span className="font-bold">Chỉnh sửa văn bản đến</span>
        </DialogTitle>
        <DialogContent>
          <div>
            <FormControl variant="standard" sx={{ mt: 1, minWidth: '100%' }}>
              <InputLabel>
                <span
                  style={{
                    display: 'inline-block',
                    color: '#ff4d4f',
                    fontSize: '14px',
                    fontFamily: 'SimSun,sans-serif',
                    lineHeight: 1,
                  }}>
                  *
                </span>
                <span> Nhóm sổ văn bản</span>
              </InputLabel>
              <Select
                name="book_group_id"
                sx={{ width: '100%' }}
                value={formik.values?.book_group_id}
                onChange={event => {
                  const bookId = event.target.value
                  handleSelectBookGroupId(bookId)
                }}
                label="Nhóm sổ văn bản">
                {bookGroupList.map(book => (
                  <MenuItem key={book.id} value={book.id}>
                    {book.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <FormControl
              variant="standard"
              sx={{ mt: 1, mb: 1, minWidth: '100%' }}>
              <InputLabel>
                <span
                  style={{
                    display: 'inline-block',
                    color: '#ff4d4f',
                    fontSize: '14px',
                    fontFamily: 'SimSun,sans-serif',
                    lineHeight: 1,
                  }}>
                  *
                </span>
                <span> Sổ văn bản</span>
              </InputLabel>
              <Select
                name="book_id"
                sx={{ width: '100%' }}
                value={formik.values?.book_id}
                onChange={event => {
                  const bookId = event.target.value
                  handleSelectBookId(bookId)
                }}
                label="Sổ văn bản">
                {bookListOfGroup.map(book => (
                  <MenuItem key={book.id} value={book.id}>
                    {book.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div style={{ marginTop: 1 }}>
            <TextField
              name="document_number"
              id="standard-basic"
              label={
                <>
                  <span
                    style={{
                      display: 'inline-block',
                      color: '#ff4d4f',
                      fontSize: '14px',
                      fontFamily: 'SimSun,sans-serif',
                      lineHeight: 1,
                    }}>
                    *
                  </span>
                  <span> Số hiệu</span>
                </>
              }
              variant="standard"
              sx={{ textAlign: 'center', height: '56px', width: '100%' }}
              value={formik.values?.document_number}
              onChange={e => {
                validateDocumentNumber(e)
              }}
              error={
                (formik.errors.document_number &&
                  formik.touched.document_number) ||
                warningSH?.status === 'WARNING'
                  ? true
                  : false
              }
            />
          </div>
          {warningSH?.status === 'WARNING' && (
            <span style={{ color: 'red' }}>
              Số hiệu văn bản đã sử dụng, vui lòng nhập số hiệu khác!
            </span>
          )}
          <div style={{ marginBottom: '4px' }}>
            <TextField
              name="incoming_number"
              id="standard-basic"
              label={
                <>
                  <span
                    style={{
                      display: 'inline-block',
                      color: '#ff4d4f',
                      fontSize: '14px',
                      fontFamily: 'SimSun,sans-serif',
                      lineHeight: 1,
                    }}>
                    *
                  </span>
                  <span> Số đến</span>
                </>
              }
              variant="standard"
              sx={{ textAlign: 'center', height: '60px', width: '100%' }}
              value={formik.values?.incoming_number}
              onChange={formik.handleChange}
              error={
                formik.errors.incoming_number && formik.touched.incoming_number
                  ? true
                  : false
              }
            />
          </div>
          <div>
            <TextField
              name="title"
              id="outlined-multiline-static"
              label={
                <>
                  <span
                    style={{
                      display: 'inline-block',
                      color: '#ff4d4f',
                      fontSize: '14px',
                      fontFamily: 'SimSun,sans-serif',
                      lineHeight: 1,
                    }}>
                    *
                  </span>
                  <span> Trích yếu</span>
                </>
              }
              multiline
              rows={3}
              // variant="filled"
              sx={{ width: '100%' }}
              value={formik.values?.title}
              onChange={formik.handleChange}
              error={formik.errors.title && formik.touched.title ? true : false}
            />
          </div>
          <div style={{ marginTop: '12px' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
              <DatePicker
                name="outgoing_date"
                label={
                  <>
                    <span
                      style={{
                        display: 'inline-block',
                        color: '#ff4d4f',
                        fontSize: '14px',
                        fontFamily: 'SimSun,sans-serif',
                        lineHeight: 1,
                      }}>
                      *
                    </span>
                    <span> Ngày đến</span>
                  </>
                }
                openTo="day"
                views={['day', 'month', 'year']}
                value={formik.values?.outgoing_date}
                onChange={handleChangeIncomingDate}
                inputFormat="DD/MM/YYYY"
                renderInput={params => (
                  <TextField
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                      },
                    }}
                    {...params}
                    error={
                      formik.errors.outgoing_date &&
                      formik.touched.outgoing_date
                        ? true
                        : false
                    }
                  />
                )}
              />
            </LocalizationProvider>
          </div>
          <div style={{ marginTop: '12px' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
              <DatePicker
                name="date_issued"
                label={
                  <>
                    <span> Ngày ban hành</span>
                  </>
                }
                openTo="day"
                views={['day', 'month', 'year']}
                value={formik.values?.date_issued}
                onChange={handleChangeDateIssued}
                inputFormat="DD/MM/YYYY"
                renderInput={params => (
                  <TextField
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                      },
                    }}
                    {...params}
                    error={false}
                  />
                )}
              />
            </LocalizationProvider>
          </div>
          <div style={{ marginTop: '12px' }}>
            <TextField
              name="signer"
              id="outlined-multiline-static"
              label=" Người ký"
              // variant="filled"
              sx={{ width: '100%' }}
              value={formik.values?.signer}
              onChange={formik.handleChange}
            />
          </div>
          <div>
            <FormControl
              variant="outlined"
              sx={{ mt: '12px', minWidth: '100%' }}>
              <InputLabel>
                <>
                  <span> Độ khẩn</span>
                </>
              </InputLabel>
              <Select
                name="urgency_level"
                sx={{ width: '100%' }}
                onChange={handleSelectChange}
                label="Độ khẩn"
                value={selectedOption}>
                <MenuItem value={''}>Bỏ chọn độ khẩn</MenuItem>
                <MenuItem value={'001'}>Khẩn</MenuItem>
                <MenuItem value={'002'}>Hỏa tốc</MenuItem>
                <MenuItem value={'003'}>Hỏa tốc hẹn giờ</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div style={{ display: 'flex' }}>
            <FormControl variant="outlined" sx={{ mt: '12px', width: '100%' }}>
              <InputLabel>
                <>
                  <span
                    style={{
                      display: 'inline-block',
                      color: '#ff4d4f',
                      fontSize: '14px',
                      fontFamily: 'SimSun,sans-serif',
                      lineHeight: 1,
                    }}>
                    *
                  </span>
                  <span> Cơ quan ban hành</span>
                </>
              </InputLabel>
              <Select
                name="authority_issued_id"
                sx={{ width: '100%' }}
                value={formik.values?.authority_issued_id}
                onChange={formik.handleChange}
                label="Cơ quan ban hành">
                {authorityIssuedsList.map(authorityIssued => (
                  <MenuItem key={authorityIssued.id} value={authorityIssued.id}>
                    {authorityIssued.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              style={{
                color: '#fff',
                backgroundColor: '#1677ff',
                marginTop: '12px',
                marginLeft: '5px',
                width: '91px',
                height: '56px',
              }}
              onClick={handleToggleAuthoritiesActionModal(true)}>
              <span style={{ fontSize: '15px' }}>Tạo mới</span>
            </Button>
          </div>
          <div style={{ marginTop: '10px' }}>
            <Upload
              valuePropName={'fileList'}
              fileList={fileList}
              multiple={true}
              beforeUpload={() => false}
              onChange={handleChangeFile}
              showUploadList={false}>
              <Button
                danger={attachmentsMessage ? true : false}
                style={{
                  color: attachmentsMessage ? null : '#fff',
                  backgroundColor: attachmentsMessage ? null : '#1677ff',
                  fontSize: '15px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                icon={<UploadOutlined />}>
                <>
                  <span>Chọn tài liệu đính kèm </span>
                  <span
                    style={{
                      display: 'inline-block',
                      color: '#ff4d4f',
                      fontSize: '18px',
                      fontFamily: 'SimSun,sans-serif',
                      lineHeight: 1,
                    }}>
                    *
                  </span>
                </>
              </Button>
            </Upload>
          </div>
          {
            <UploadFileListWrapper>
              {renderListFile}
              {renderListFileUpdate}
            </UploadFileListWrapper>
          }
        </DialogContent>
        <DialogActions
          sx={{ backgroundColor: '#e3e4e9', position: 'sticky', bottom: 0 }}>
          <Button
            sx={{ fontWeight: 600 }}
            onClick={handleCloseForm}
            className="uppercase w-[94px] h-[40px] text-sm font-semibold leading-5 text-primary">
            Đóng
          </Button>
          {warningSH?.status === 'WARNING' ? (
            <button
              disabled={true}
              sx={{ fontWeight: 600 }}
              className="opacity-[0.3] uppercase w-[94px] h-[40px] rounded-[100px] text-sm font-semibold leading-5 text-white bg-[#0073C7]">
              {' '}
              Cập nhật
            </button>
          ) : (
            <button
              type="submit"
              sx={{ fontWeight: 600 }}
              className="uppercase w-[94px] h-[40px] rounded-[100px] text-sm font-semibold leading-5 text-white bg-[#0073C7]">
              {' '}
              Cập nhật
            </button>
          )}
        </DialogActions>
      </form>
      <PopupRenameFile
        isModalVisibleRenameFile={isModalVisibleRenameFile}
        handleCloseModalRenameFile={() => setIsModalVisibleRenameFile(false)}
        submitRenameFile={submitRenameFile}
        fieldsRenameFile={fieldsRenameFile}
        fileExt={fileExt}
      />
      {isVisibleAuthoritiesActionModal && (
        <AuthoritiesActionModal
          disabledAuthoritiesTypePicker
          tabActive={DOCUMENT_TYPE.INCOMING}
          payloadModal={{
            status: true,
            isCreate: true,
            updatePayLoad: null,
          }}
          handleCloseModal={handleToggleAuthoritiesActionModal(false)}
        />
      )}
    </Dialog>
  )
}

export default memo(
  inject(
    'authorityStore',
    'bookStore',
    'loadingAnimationStore',
    'commonStore',
    'fileStore',
    'signedDocumentStore',
    'internalDocumentStore'
  )(observer(InternalDocumentIncomingUpdate))
)
